import useResizeObserver from '@react-hook/resize-observer';
import { Dialog, Loader, useDialog } from 'components';
import { Popover } from 'components/organization/popover/popover';
import { useSelectedLocation } from 'contexts';
import * as d3 from 'd3';
import {
  Dispatch,
  FC,
  Fragment,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useNavigate } from 'react-router';
import fetchWithRetry from 'services/fetch-retry';
import { Filter, FilterOperator, FilterState, LogicalOperator } from 'utils';
import { ChartComponentProps } from '../chart-card';

type RectangularNode = d3.HierarchyRectangularNode<TreemapData>;
type Selection<T extends d3.BaseType = SVGElement | null> = d3.Selection<T, TreemapData, T, TreemapData | undefined>;
type Transition<T extends d3.BaseType = SVGElement | null> = d3.Transition<T, TreemapData, T, TreemapData | undefined>;

class TreemapData {
  name: string;
  value: number;
  children: TreemapData[];
  clipUid?: number;
  leafUid?: number;
  property?: string;
  constructor(treeMapData?: Partial<TreemapData>) {
    this.name = treeMapData?.name ?? '';
    this.value = treeMapData?.value ?? 0;
    this.children = Object.assign([], treeMapData?.children).map(child => new TreemapData(child));
  }
}

class PropertyTier {
  carbCompliant: number = -6;
  customer: number = 4;
  loaded: number = -7;
  onHold: number = -5;
  sizeType: number = 1;
  unitGrade: number = -2;
  unitStatus: number = 3;
}

const isKeyOfPropertyTier = (key: any): key is keyof PropertyTier => !!key && key in new PropertyTier();

const ROOT_OFFSET = 32;

const CustomizeGraph: FC<{ propertyTier: PropertyTier; setPropertyTier: Dispatch<SetStateAction<PropertyTier>> }> = ({
  propertyTier,
  setPropertyTier
}) => {
  const [tiers, setTiers] = useState({ ...propertyTier });
  const [drag, setDrag] = useState<keyof PropertyTier>();
  const popoverRef = useRef<{ hide: () => void }>(null);
  return (
    <Popover
      ref={popoverRef}
      triggerClassNames="btn-back p-2"
      trigger={<span className="material-icons icon-large">settings</span>}
      content={
        <div>
          <h4>Customize Graph</h4>
          <div className="d-flex flex-column">
            {Object.entries(tiers)
              .sort((...es) => Math.abs(es[0][1]) - Math.abs(es[1][1]))
              .map(
                ([prop, tier]) =>
                  isKeyOfPropertyTier(prop) && (
                    <div
                      key={prop + tier}
                      className={`d-flex align-items-center g-2 p-2 white-space-nowrap dragging-${drag === prop}`}
                      onDragOver={e => {
                        e.preventDefault();
                        if (drag && drag !== prop) {
                          setTiers(prevState => ({
                            ...prevState,
                            [prop]: (1 - 2 * Number(prevState[prop] < 0)) * Math.abs(prevState[drag]),
                            [drag]: (1 - 2 * Number(prevState[drag] < 0)) * Math.abs(prevState[prop])
                          }));
                        }
                      }}
                      onDrop={e => {
                        e.preventDefault();
                        setDrag(undefined);
                      }}
                    >
                      <input
                        id={`${prop}-checkbox`}
                        type="checkbox"
                        checked={tier >= 0}
                        onChange={e =>
                          setTiers(prevState => ({
                            ...prevState,
                            [prop]: (-1 + 2 * Number(e.target.checked)) * Math.abs(prevState[prop])
                          }))
                        }
                      />
                      <label className="flex-grow-1 text-info-main text-uppercase" htmlFor={`${prop}-checkbox`}>
                        {prop.replace(/([a-z])([A-Z])/g, '$1 $2')}
                      </label>
                      <span className="material-icons drag-handle" draggable onDragStart={() => setDrag(prop)}>
                        drag_handle
                      </span>
                    </div>
                  )
              )}
          </div>
          <div className="d-flex justify-content-end mt-2">
            <button
              className="btn-primary"
              onClick={() => {
                setPropertyTier(tiers);
                popoverRef.current?.hide();
              }}
            >
              APPLY
            </button>
          </div>
        </div>
      }
    />
  );
};

const ZoomableTreemap: FC<ChartComponentProps> = ({ boxRef, filter, focused, setTitleElement }) => {
  const navigate = useNavigate();
  const { selectedLocation } = useSelectedLocation();

  const filterDialog = useDialog();
  const settingsDialog = useDialog();

  const [data, setData] = useState<TreemapData>(new TreemapData());
  const [grouping, setGrouping] = useState<PropertyTier>(new PropertyTier());
  const [loading, setLoading] = useState<boolean>(false);
  const [scaleQuantile, setScaleQuantile] = useState<boolean>(false);

  const svgRef = useRef<SVGSVGElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  /* incrementing unique id */
  const uid = useRef(0);
  /* filter path */
  const [selectedNode, setSelectedNode] = useState<RectangularNode | null>(null);

  /* set card title buttons */
  useEffect(() => {
    setTitleElement?.(
      <div className="flex-grow-1 d-flex align-items-center justify-content-end">
        <div id="scale-toggle" className="material-icons icon-large p-2">
          <input type="checkbox" checked={scaleQuantile} onChange={e => setScaleQuantile(e.target.checked)} />
        </div>
        <CustomizeGraph propertyTier={grouping} setPropertyTier={setGrouping} />
      </div>
    );
  }, [grouping, settingsDialog.openDialog, scaleQuantile, setTitleElement]);

  /* fetch data on hierarchy order change */
  useEffect(() => {
    const groupingOrder = Object.entries(grouping)
      .sort((...es) => Math.abs(es[0][1]) - Math.abs(es[1][1]))
      .filter(e => e[1] >= 0)
      .map(e => e[0]);
    setLoading(true);
    const url = new URL('/api/units/counts', window.location.origin);
    selectedLocation && url.searchParams.append('locationId', `${selectedLocation.id}`);
    url.searchParams.append(
      'properties',
      groupingOrder.join(',')
    );

    const addPropertyName = (children: TreemapData[], level: number): TreemapData[] => 
      children.map(child => ({ ...child, property: groupingOrder[level], children: addPropertyName(child.children, level + 1) }))
    
    fetchWithRetry<TreemapData>(url, { method: 'GET' }, new TreemapData())
      .then(res => new TreemapData(res))
      .then(data => {
        setData({
          ...data,
          children: addPropertyName(data.children, 0)
        });
      })
      .finally(() => setLoading(false));
  }, [grouping, selectedLocation, setData, setLoading]);

  /* svg dimensions */
  const [{ height, width }, setDimensions] = useState({ height: 0, width: 0 });
  useLayoutEffect(() => {
    setDimensions(() => ({
      width: boxRef.current?.clientWidth ?? 0,
      height: boxRef.current?.clientHeight ?? 0
    }));
  }, [boxRef]);
  useResizeObserver(boxRef.current, ({ contentBoxSize }) => {
    setDimensions({
      width: contentBoxSize[0].inlineSize,
      height: contentBoxSize[0].blockSize
    });
  });

  // Compute the layout.
  const hierarchy: d3.HierarchyNode<TreemapData> = useMemo(() => {
    return d3
      .hierarchy(data)
      .sum(d => d.value ?? 0)
      .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  }, [data]);

  /* keep track of zoom for rerenders */
  const selectedRoot: MutableRefObject<RectangularNode | null> = useRef(null);

  const rootNode: RectangularNode = useMemo(() => {
    selectedRoot.current = null;
    return d3.treemap<TreemapData>().tile((node: RectangularNode, x0: number, y0: number, x1: number, y1: number) => {
      d3.treemapSlice(node, 0, 0, width, height);
      node.children?.forEach(child => {
        child.x0 = x0 + (child.x0 / width) * (x1 - x0);
        child.x1 = x0 + (child.x1 / width) * (x1 - x0);
        child.y0 = y0 + (child.y0 / height) * (y1 - y0);
        child.y1 = y0 + (child.y1 / height) * (y1 - y0);
      });
    })(hierarchy);
  }, [hierarchy, height, width]);

  /* get filters for selected node and ancestors (zoomed nodes) */
  const computeFilters = useCallback(
    (d?: RectangularNode | null): Filter[] => {
      let filter: Filter = { field: '', filterOperator: FilterOperator.EQUALS, value: d?.data.name };
      if (!d || d === rootNode) {
        return [];
      } else {
        filter.field = d.data.property ?? '';
      }
      return [filter].concat(computeFilters(d.parent));
    },
    [rootNode]
  );

  const filterState: FilterState = useMemo(
    () => ({
      logicalOperator: LogicalOperator.AND,
      filters: computeFilters(selectedNode)
    }),
    [computeFilters, selectedNode]
  );

  useEffect(() => {
    const root = selectedRoot.current ?? rootNode; /* use previous (zoomed to) root if set */
    const svg: Selection<SVGSVGElement | null> = d3.select(svgRef.current);
    svg.selectAll('g').remove();

    // Create the scales.
    const x = d3.scaleLinear([0, width]);
    const y = !scaleQuantile
      ? d3.scaleLinear().domain([root.y0, root.y1]).range([0, height])
      : d3.scaleQuantile(root.children?.flatMap(c => [c.y0, c.y1]) ?? [], d3.range(height));

    // Formatting utilities.
    const name = (d: RectangularNode) =>
      d
        .ancestors()
        .reverse()
        .map(d => d.data.name);

    // Create the SVG container.
    svg
      .attr('viewBox', [0, -ROOT_OFFSET, width, height + ROOT_OFFSET])
      .attr('width', width)
      .attr('height', height)
      .attr('style', 'max-width: 100%; height: auto;');

    // Display the root.
    let group = svg.append('g').call(render, root);

    function render(g: Selection<any>, r: RectangularNode) {
      const node = g
        .selectAll('g')
        .data(r.children?.concat(r) ?? [])
        .join('g');

      node
        .filter(d => !!(d === r ? d.parent : d.children))
        .attr('cursor', 'pointer')
        .on('mousemove', (e, d) => {
          const tooltip = d3.select(tooltipRef.current);
          const size = tooltip.node()?.getBoundingClientRect();
          const x = Math.max(0, Math.min(width - (size?.width ?? 0), e.offsetX));
          const y = Math.max(0 + (size?.height ?? 0), Math.min(height, e.offsetY));
          tooltip
            .style('transform', `translate(${x}px,calc(${y}px - 100%))`)
            .style('opacity', 1)
            .html(name(d).concat(`${d.value}`).join(`\n`));
        })
        .on('mouseout', (_, d) => d3.select(tooltipRef.current).style('opacity', 0))
        .on('click', (_, d) => (d === r ? zoomout(r) : zoomin(d)));

      // node.append('title').text(d => name(d).concat(`${d.value}`).join(`\n`));

      const fader = (color: any) => d3.interpolateRgb(color, '#fff')(0.3);
      const sizeTypeScale = d3.scaleOrdinal(['#366375', '#4E8392', '#7BA2AD', '#A7C1C8', '#D3E0E3'].map(fader));
      const unitGradeScale = d3.scaleOrdinal(['#26296B', '#3B3E79', '#515388', '#676997', '#9294B5'].map(fader));
      const unitStatusScale = d3.scaleOrdinal(
        [
          '#1223B2',
          '#1428CC',
          '#172DE5',
          '#1A32FF',
          '#3046FF',
          '#475AFF',
          '#5E6FFF',
          '#7584FF',
          '#8C98FF',
          '#A3ADFF',
          '#BAC1FF',
          '#D1D6FF',
          '#E8EAFF'
        ].map(fader)
      );
      const colorScale = d3.scaleOrdinal(['#195CA2', '#004A98', '#14284D'].map(fader));
      const getColor = (d: RectangularNode) => {
        if (d === r) {
          return '#fff';
        }
        switch (
          Object.entries(grouping)
            .filter(e => e[1] > 0)
            .sort((e0, e1) => e0[1] - e1[1])[d.depth - 1][0]
        ) {
          case 'sizeType':
            return sizeTypeScale(d.data.name);
          case 'unitGrade':
            return unitGradeScale(d.data.name);
          case 'unitStatus':
            return d.data.name.includes('AVAILABLE') ? '#0F8E49' : unitStatusScale(d.data.name);
          case 'carbCompliant':
          case 'customer':
          case 'loaded':
          case 'onHold':
          default:
            return colorScale(d.data.name);
        }
      };
      const getTextColor = (d: RectangularNode) => {
        const background = d3.color(getColor(d));
        if (background) {
          const RsRGB = background.rgb().r / 255;
          const GsRGB = background.rgb().g / 255;
          const BsRGB = background.rgb().b / 255;
          const R = RsRGB <= 0.03928 ? RsRGB / 12.92 : Math.pow((RsRGB + 0.055) / 1.055, 2.4);
          const G = GsRGB <= 0.03928 ? GsRGB / 12.92 : Math.pow((GsRGB + 0.055) / 1.055, 2.4);
          const B = BsRGB <= 0.03928 ? BsRGB / 12.92 : Math.pow((BsRGB + 0.055) / 1.055, 2.4);
          const luminance = 0.2126 * R + 0.7152 * G + 0.0722 * B;
          if (luminance > Math.sqrt(1.05 * 0.5)) return '#000';
        }
        return '#fff';
      };

      node
        .append('rect')
        .attr('id', d => (d.data.leafUid = uid.current++))
        // .attr('fill', d => (d === r ? '#fff' : d.children ? '#ccc' : '#ddd'))
        .attr('fill', getColor)
        .attr('stroke', '#fff');

      node
        .append('clipPath')
        .attr('id', d => (d.data.clipUid = uid.current++))
        .append('use')
        .attr('xlink:href', d => `${d.data.leafUid}`);

      node
        .append('text')
        .attr('fill', getTextColor)
        .attr('clip-path', d => `${d.data.clipUid}`)
        .attr('font-weight', d => (d === r ? 'bold' : null))
        .selectAll('tspan')
        .data(d =>
          (d === r ? name(d) : [d.data.name]).concat([`${d.value ?? 0}`]).flatMap((v, i) => (!i ? [v] : [' | ', v]))
        )
        .join('tspan')
        .attr('dx', 4)
        .attr('y', 16)
        .attr('fill-opacity', (_, i, nodes) => (i === nodes.length - 1 ? 0.7 : null))
        .attr('font-weight', (_, i, nodes) => (i === nodes.length - 1 ? 'normal' : null))
        .text(d => d);

      // append icon to top right - navigate to grid on click
      node
        .filter(d => d !== r)
        .append('text')
        .attr('transform', 'translate(-32)')
        .attr('x', '100%')
        .attr('y', '32')
        .attr('class', 'material-icons icon-large')
        .on('mouseenter', function () {
          d3.select(this).attr('fill', 'var(--primary)');
        })
        .on('mouseout', function () {
          d3.select(this).attr('fill', null);
        })
        .on('click', (e: PointerEvent, d: RectangularNode) => {
          e.preventDefault();
          e.stopPropagation();
          setSelectedNode(d);
          filterDialog.openDialog();
        })
        .text('filter_list')
        .append('title')
        .text('Yard Management');

      g.call(position, r);
    }

    function position(g: any, r: RectangularNode) {
      selectedRoot.current = r;
      g.selectAll('g')
        .attr('transform', (d: RectangularNode) =>
          d === r ? `translate(0,-${ROOT_OFFSET})` : `translate(${x(d.x0)},${y(d.y0)})`
        )
        .select('rect')
        .attr('width', (d: RectangularNode) => (d === r ? width : x(d.x1) - x(d.x0)))
        .attr('height', (d: RectangularNode) => (d === r ? ROOT_OFFSET : y(d.y1) - y(d.y0)));
    }

    // When zooming in, draw the new nodes on top, and fade them in.
    function zoomin(d: RectangularNode) {
      const g = group.attr('pointer-events', 'none');
      group = svg.append('g').call(render, d);

      x.domain([d.x0, d.x1]);
      y.domain(!scaleQuantile ? [d.y0, d.y1] : d.children?.flatMap(c => [c.y0, c.y1]) ?? []);

      svg
        .transition()
        .duration(750)
        .call((t: Transition<any>) => g.transition(t).remove().call(position, d.parent))
        .call((t: Transition<any>) =>
          group
            .transition(t)
            .attrTween('opacity', () => d3.interpolate(0, 1) as any)
            .call(position, d)
        );
    }

    // When zooming out, draw the old nodes on top, and fade them out.
    function zoomout(d: RectangularNode) {
      const g = group.attr('pointer-events', 'none');
      group = svg.insert('g', '*').call(render, d.parent ?? rootNode);

      x.domain([d.parent?.x0 ?? 0, d.parent?.x1 ?? 1]);
      y.domain(
        !scaleQuantile ? [d.parent?.y0 ?? 0, d.parent?.y1 ?? 1] : d.parent?.children?.flatMap(c => [c.y0, c.y1]) ?? []
      );

      svg
        .transition()
        .duration(750)
        .call((t: Transition<any>) =>
          g
            .transition(t)
            .remove()
            .attrTween('opacity', () => d3.interpolate(1, 0) as any)
            .call(position, d)
        )
        .call((t: Transition<any>) => group.transition(t).call(position, d.parent));
    }
  }, [filterDialog, grouping, height, rootNode, scaleQuantile, width]);

  return (
    <div className="h-100" id="zoomable-treemap">
      <Loader loading={loading}>
        <div className="tooltip p-3" ref={tooltipRef} />
        <svg ref={svgRef} />
      </Loader>
      <Dialog closeDialog={filterDialog.closeDialog} ref={filterDialog.ref}>
        <div className="d-flex flex-column g-3">
          <h4 className="text-info-dark">GO TO YARD MANAGEMENT?</h4>
          <h5 className="text-info-main">APPLY FILTERS ({selectedNode?.value ?? 0} UNITS)</h5>
          <dl className="grid-1 my-1">
            {filterState.filters.map(filter => (
              <Fragment key={filter.field}>
                <dt className="text-info-main text-uppercase">{filter.field?.replace(/([a-z])([A-Z])/g, '$1 $2')}</dt>
                <dd className="text-info-dark">{filter.value}</dd>
              </Fragment>
            ))}
          </dl>
          <div className="grid-3">
            <button className="btn-outline col-start-2" onClick={filterDialog.closeDialog}>
              CANCEL
            </button>
            <button className="btn-primary col-start-3"
              onClick={() => navigate('/units', { state: { filterState } })}
            >
              CONTINUE
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ZoomableTreemap;
